// Window-level drag-enter detector — tracks whether a file is being dragged over
// the browser window so the caller can show a full-screen drop overlay.
//
// The hook does NOT call any addFiles on the window drop event — that's intentional.
// The actual file handling is done by whichever drop target fires (the overlay's
// onDrop, or the composer's local onDrop). Calling it here too would cause a double
// upload because both the window listener and the element handler fire for the same
// drop event.

import { useState, useEffect, useRef, useCallback } from 'react'

export function useWindowDrop() {
  const [dragging, setDragging] = useState(false)
  // Counter tracks nested enter/leave events so we don't flicker on child elements.
  const count = useRef(0)

  useEffect(() => {
    const enter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return
      if (count.current === 0) setDragging(true)
      count.current++
    }
    const leave = () => {
      count.current = Math.max(0, count.current - 1)
      if (count.current === 0) setDragging(false)
    }
    const over = (e: DragEvent) => { e.preventDefault() }
    const drop = (e: DragEvent) => {
      // Prevent the browser from navigating to the dropped file. State cleanup only —
      // actual file handling happens in whichever React element receives the drop.
      e.preventDefault()
      count.current = 0
      setDragging(false)
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragleave', leave)
    window.addEventListener('dragover', over)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('dragover', over)
      window.removeEventListener('drop', drop)
    }
  }, [])

  const overlayLeave = useCallback(() => { count.current = 0; setDragging(false) }, [])

  return { dragging, overlayLeave }
}
